import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, Send, Plus, ChevronUp, ChevronDown, X } from 'lucide-react'
import { courseApi, lessonApi } from '../api/lessonBuilderApi'
import { countCards } from '../lib/sections'
import { compressImageFile } from '../../../utils/imageCompress'
import FileDropzone from '../components/FileDropzone'
import type { CourseDoc } from '../types/CourseDoc'
import type { LessonDoc } from '../types/LessonDoc'

const IMG_ACCEPT = { 'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.gif'] }

// Trang soạn khoá học: thông tin khoá + chọn/sắp xếp bài giảng.
const CourseBuilderPage: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [course, setCourse] = useState<CourseDoc | null>(null)
  const [allLessons, setAllLessons] = useState<LessonDoc[]>([])
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [coverBusy, setCoverBusy] = useState(false)

  useEffect(() => {
    if (id) courseApi.get(id).then(setCourse)
    lessonApi.list().then(setAllLessons)
  }, [id])

  if (!course) {
    return <div className="p-10 text-center text-gray-400">Không tìm thấy khoá học.</div>
  }

  const update = (patch: Partial<CourseDoc>) => setCourse({ ...course, ...patch })

  const lessonsInCourse = course.lessonIds
    .map((lid) => allLessons.find((l) => l.id === lid))
    .filter(Boolean) as LessonDoc[]
  const availableLessons = allLessons.filter((l) => !course.lessonIds.includes(l.id))

  const addLesson = (lid: string) => update({ lessonIds: [...course.lessonIds, lid] })
  const removeLesson = (lid: string) => update({ lessonIds: course.lessonIds.filter((x) => x !== lid) })
  const moveLesson = (lid: string, dir: -1 | 1) => {
    const ids = [...course.lessonIds]
    const i = ids.indexOf(lid)
    const j = i + dir
    if (i < 0 || j < 0 || j >= ids.length) return
    ;[ids[i], ids[j]] = [ids[j], ids[i]]
    update({ lessonIds: ids })
  }

  const handleSave = async (publish: boolean) => {
    if (!course.title.trim()) {
      alert('Vui lòng nhập tên khoá học.')
      return
    }
    const saved = await courseApi.save(course)
    setCourse(saved)
    setSavedAt(new Date().toLocaleTimeString('vi-VN'))
    if (publish) {
      const pub = await courseApi.publish(saved.id)
      navigate(`/khoa-hoc/${(pub || saved).slug}`)
    }
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="sticky top-0 z-20 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/teacher/khoa-hoc')} className="text-gray-400 hover:text-gray-700">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-black text-gray-900">Soạn khoá học</h1>
          {savedAt && <span className="text-xs text-gray-400">Đã lưu lúc {savedAt}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSave(false)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200"
          >
            <Save size={16} /> Lưu nháp
          </button>
          <button
            onClick={() => handleSave(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700"
          >
            <Send size={16} /> Xuất bản
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4 max-w-6xl mx-auto">
        {/* Thông tin khoá */}
        <section className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider">Thông tin khoá học</h3>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Tên khoá học</label>
              <input
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                value={course.title}
                onChange={(e) => update({ title: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Mô tả</label>
              <textarea
                className="w-full min-h-[90px] p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                value={course.description || ''}
                onChange={(e) => update({ description: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Ảnh bìa khoá học</label>
              {course.coverImage ? (
                <div className="relative mt-1 group">
                  <img src={course.coverImage} alt="Ảnh bìa" className="w-full h-36 object-cover rounded-xl border border-gray-200" />
                  <button
                    onClick={() => update({ coverImage: undefined })}
                    className="absolute top-2 right-2 px-2 py-1 rounded-lg bg-white/90 text-red-600 text-xs font-bold shadow hover:bg-white"
                  >
                    Xoá ảnh
                  </button>
                </div>
              ) : (
                <FileDropzone
                  accept={IMG_ACCEPT}
                  busy={coverBusy}
                  label="Kéo-thả ảnh bìa"
                  hint="PNG, JPG, WebP — nên dùng ảnh ngang (16:9)"
                  onFile={async (file) => {
                    setCoverBusy(true)
                    try {
                      update({ coverImage: await compressImageFile(file, { maxWidth: 1600, maxHeight: 900 }) })
                    } finally {
                      setCoverBusy(false)
                    }
                  }}
                />
              )}
              <input
                className="w-full mt-2 p-2 border border-gray-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-200"
                value={course.coverImage && !course.coverImage.startsWith('data:') ? course.coverImage : ''}
                onChange={(e) => update({ coverImage: e.target.value || undefined })}
                placeholder="hoặc dán URL ảnh bìa"
              />
            </div>
          </div>
        </section>

        {/* Bài giảng trong khoá + thêm bài */}
        <section className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider mb-3">
              Bài giảng trong khoá ({lessonsInCourse.length})
            </h3>
            {lessonsInCourse.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">Chưa có bài. Thêm từ danh sách bên dưới.</p>
            ) : (
              <div className="space-y-2">
                {lessonsInCourse.map((l, i) => (
                  <div key={l.id} className="flex items-center gap-2 p-2.5 rounded-xl border border-gray-100">
                    <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center shrink-0">
                      {i + 1}
                    </span>
                    <span className="flex-1 text-sm font-semibold text-gray-800 truncate">{l.meta.tieuDe}</span>
                    <button onClick={() => moveLesson(l.id, -1)} disabled={i === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                      <ChevronUp size={16} />
                    </button>
                    <button onClick={() => moveLesson(l.id, 1)} disabled={i === lessonsInCourse.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                      <ChevronDown size={16} />
                    </button>
                    <button onClick={() => removeLesson(l.id)} className="p-1 text-gray-400 hover:text-red-600">
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider mb-3">Bài giảng có sẵn</h3>
            {availableLessons.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">
                Không còn bài nào. Tạo bài giảng ở mục "Bài giảng của tôi".
              </p>
            ) : (
              <div className="space-y-2">
                {availableLessons.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => addLesson(l.id)}
                    className="w-full flex items-center gap-2 p-2.5 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50/40 text-left"
                  >
                    <Plus size={16} className="text-blue-600 shrink-0" />
                    <span className="flex-1 text-sm font-semibold text-gray-800 truncate">{l.meta.tieuDe}</span>
                    <span className="text-[11px] text-gray-400">{countCards(l)} thẻ</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

export default CourseBuilderPage
